'use client'

import { TagManagementDropdown } from '@/components/Tags/TagManagementDropdown'
import { TagPill } from '@/components/Tags/TagPill'
import { PortalDropdown } from '@/components/ui/PortalDropdown'
import { getIntegrationConfig } from '@/lib/integrations'
import { cn } from '@/lib/utils'
import { Pencil } from 'lucide-react'
import { Space_Grotesk } from 'next/font/google'
import Link from 'next/link'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import AppTemplateMenu from '../DashboardV2/AppTemplateMenu'
import ThreadBookmarkButton from '../ui/ThreadBookmarkButton'

const spaceGrotesk = Space_Grotesk({
  subsets: ['latin'],
  weight: ['400', '700'],
  variable: '--font-space-grotesk',
})

function formatDate(date: string) {
  const now = new Date()
  const updated = new Date(date)
  const diffTime = Math.abs(now.getTime() - updated.getTime())
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  const diffHours = Math.floor(diffTime / (1000 * 60 * 60))

  if (diffDays > 1) {
    return `${diffDays} days ago`
  } else if (diffHours > 0) {
    return `${diffHours} hours ago`
  }

  return 'just now'
}

export interface ThreadTemplateProps {
  className?: string
  description?: string
  title: string
  isActive?: boolean
  href: string
  id?: string
  updatedAt?: string
  onDeleteAction?: () => void
  onDuplicateAction?: () => void
  onRenameAction?: (newName: string) => Promise<void> | void
  isBookmarked?: boolean
  onBookmarkToggle?: () => Promise<void> | void
  hideBookmarkButton?: boolean
  tags?: Array<{ id: string; name: string; workspaceId: string | null }>
  onEditTags?: (event: React.MouseEvent) => void
  isTagDropdownOpen?: boolean
  workspaceId?: string
  onCloseTagDropdown?: () => void
  dropdownPosition?: { x: number; y: number } | null
  requiredIntegrations?: string[]
}

const ThreadTemplate: React.FC<ThreadTemplateProps> = ({
  className,
  description = 'No description available',
  title,
  isActive = true,
  href,
  id,
  updatedAt,
  onDeleteAction,
  onDuplicateAction,
  onRenameAction,
  isBookmarked = false,
  onBookmarkToggle,
  hideBookmarkButton = false,
  tags,
  onEditTags,
  isTagDropdownOpen = false,
  workspaceId,
  onCloseTagDropdown,
  dropdownPosition,
  requiredIntegrations,
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editedTitle, setEditedTitle] = useState(title)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const adjustedDropdownPosition = useMemo(() => {
    if (!dropdownPosition) return null
    return {
      top: Math.min(
        dropdownPosition.y,
        typeof window !== 'undefined' ? window.innerHeight - 400 : dropdownPosition.y
      ),
      left: Math.min(
        dropdownPosition.x,
        typeof window !== 'undefined' ? window.innerWidth - 320 : dropdownPosition.x
      ),
    }
  }, [dropdownPosition])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onCloseTagDropdown?.()
      }
    }

    if (isTagDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isTagDropdownOpen, onCloseTagDropdown])

  const handleRename = async () => {
    if (editedTitle.trim() && editedTitle !== title && onRenameAction) {
      await onRenameAction(editedTitle.trim())
    }
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleRename()
    } else if (e.key === 'Escape') {
      setEditedTitle(title)
      setIsEditing(false)
    }
  }

  return (
    <div className={cn('h-30 relative w-full', className)}>
      {
        <Link
          href={href}
          className={cn(
            'group relative flex h-full w-full items-center rounded-2xl ' +
              'cursor-pointer text-left transition-transform duration-300 ease-out focus:outline-none',
            isActive && 'hover:scale-[102.5%] focus:scale-100 active:scale-[97.5%]'
          )}
        >
          {/* backdrop */}
          <div
            className={cn(
              'absolute inset-0 rounded-2xl bg-white/80 backdrop-blur-xl ' +
                'shadow-lg transition-all duration-300',
              isActive &&
                'group-hover:bg-white/90 group-hover:shadow-xl group-hover:backdrop-blur-2xl'
            )}
          />

          <div className="relative z-10 flex h-full w-full items-center px-6 py-1">
            {/* text + actions */}
            <div className="flex h-full min-w-0 flex-1 flex-col py-3 pr-8">
              <div className="min-w-0 flex-shrink-0">
                <div className="mb-1 flex min-w-0 items-center gap-2">
                  {isEditing ? (
                    <div
                      className="flex-1"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                    >
                      <input
                        ref={inputRef}
                        type="text"
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e.target.value)}
                        onBlur={handleRename}
                        onKeyDown={handleKeyDown}
                        className={cn(
                          spaceGrotesk.className,
                          'w-full rounded border border-gray-300 bg-white px-2 py-1 text-lg font-bold tracking-tight text-gray-900 focus:border-gray-500 focus:outline-none'
                        )}
                      />
                    </div>
                  ) : (
                    <>
                      <h3
                        className={cn(
                          spaceGrotesk.className,
                          'line-clamp-1 flex-1 text-lg font-bold tracking-tight text-gray-900 ' +
                            'transition-colors duration-300 group-hover:text-gray-800'
                        )}
                      >
                        {title}
                      </h3>
                      {onRenameAction && (
                        <button
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setIsEditing(true)
                          }}
                          className="flex-shrink-0 rounded p-1 text-gray-400 opacity-0 transition-all hover:bg-gray-100 hover:text-gray-600 group-hover:opacity-100"
                          aria-label="Rename"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                    </>
                  )}
                </div>
                {((tags && tags.length > 0) ||
                  (requiredIntegrations && requiredIntegrations.length > 0)) && (
                  <div className="mb-1 flex flex-wrap items-center gap-1">
                    {tags?.map((tag) => (
                      <TagPill
                        key={tag.id}
                        name={tag.name}
                        isGlobal={tag.workspaceId === null}
                        readonly
                      />
                    ))}
                    {requiredIntegrations?.map((integration) => {
                      const config = getIntegrationConfig(integration)
                      if (!config) return null
                      const IconComponent = config.icon
                      return (
                        <div
                          key={integration}
                          className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2 py-0.5"
                          title={`Requires ${config.displayName}`}
                        >
                          <IconComponent
                            className="h-3 w-3"
                            style={
                              config.shouldColorIcon ? { color: config.primaryColor } : undefined
                            }
                          />
                          <span className="text-xs text-gray-600">{config.displayName}</span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
              <div className="min-h-0 flex-1 overflow-hidden">
                <p className="line-clamp-2 overflow-ellipsis text-sm text-gray-600 transition-colors duration-300 group-hover:text-gray-700">
                  {description}
                </p>
              </div>
              <div className="flex-shrink-0">
                {updatedAt && (
                  <p className="line-clamp-2 pt-2 font-mono text-xs leading-snug tracking-tight text-gray-400 group-hover:line-clamp-5">
                    Edited {formatDate(updatedAt)}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Bookmark button - top right */}
          {onBookmarkToggle && !hideBookmarkButton && (
            <div className="absolute right-4 top-4 z-20">
              <ThreadBookmarkButton isBookmarked={isBookmarked} onToggle={onBookmarkToggle} />
            </div>
          )}

          {/* More options button - bottom right */}
          {(onDeleteAction || id || onDuplicateAction || onRenameAction || onBookmarkToggle) && (
            <div className="absolute bottom-4 right-4 z-20">
              <AppTemplateMenu
                id={id}
                onDeleteAction={onDeleteAction}
                onDuplicateAction={onDuplicateAction}
                onEditTags={onEditTags}
                onRenameAction={onRenameAction ? () => setIsEditing(true) : undefined}
                isBookmarked={isBookmarked}
                onBookmarkToggle={onBookmarkToggle}
                bookmarkVariant="bookmark"
              />
            </div>
          )}
        </Link>
      }
      {!isActive && (
        <div className="absolute inset-0 rounded-2xl bg-gray-400/40 backdrop-blur-xl" />
      )}

      {/* Tag dropdown portal */}
      {workspaceId && id && adjustedDropdownPosition && (
        <PortalDropdown
          isOpen={isTagDropdownOpen}
          position={adjustedDropdownPosition}
          dropdownRef={dropdownRef}
        >
          <TagManagementDropdown
            ref={dropdownRef}
            isOpen={isTagDropdownOpen}
            workflowId={id}
            workspaceId={workspaceId}
            currentTags={tags || []}
          />
        </PortalDropdown>
      )}
    </div>
  )
}

export default ThreadTemplate
